/**
 * Canonical React Query key factory. Every read in the app should key off
 * this structure so RealtimeSupervisor invalidations reach the right caches.
 *
 * Structure: every key starts with `queryKeys.all` (`['contruly']`) so a
 * single `invalidateQueries({ queryKey: queryKeys.all })` nukes the world
 * (e.g. on logout). Sub-namespaces follow domain, not technical layer.
 */
export const queryKeys = {
  all: ['contruly'] as const,

  // ─── Brand (authenticated reads) ───
  brands: (userId: string) => [...queryKeys.all, 'brands', userId] as const,
  brand: (brandId: string) => [...queryKeys.all, 'brand', brandId] as const,
  brandByHandle: (handle: string) => [...queryKeys.all, 'brand', 'handle', handle] as const,
  brandMembers: (brandId: string) => [...queryKeys.all, 'brand-members', brandId] as const,
  // The words on the generated site's home page — the owner's editing read.
  // Separate from `brand` because the public site reads the same columns under
  // its own anon key; both invalidate off the v2_brands realtime row.
  brandSiteIdentity: (brandId: string) =>
    [...queryKeys.all, 'brand', 'site-identity', brandId] as const,
  // One audience's client-facing words — the owner's editing read, separate from
  // the public per-slug read the site itself uses.
  audienceWords: (audienceId: string) =>
    [...queryKeys.all, 'audience', 'words', audienceId] as const,
  // A creator's names for their audiences, read only (Insights' audience switch).
  audienceLabels: (userId: string) =>
    [...queryKeys.all, 'audience', 'labels', userId] as const,

  // ─── Profile (current user's account row; durable source of truth for avatar) ───
  profile: (userId: string) => [...queryKeys.all, 'profile', userId] as const,

  // ─── Change log: its readers key on ['change-log', …] (useChangeLog), so this
  //     root invalidates every one of them by prefix ───
  changeLogRoot: () => ['change-log'] as const,

  // ─── Security (sign-in methods, second factors, session assurance) ───
  authIdentities: (userId: string) => [...queryKeys.all, 'auth', 'identities', userId] as const,
  authFactors: (userId: string) => [...queryKeys.all, 'auth', 'factors', userId] as const,
  authAssurance: (userId: string) => [...queryKeys.all, 'auth', 'assurance', userId] as const,
  authPasskeys: (userId: string) => [...queryKeys.all, 'auth', 'passkeys', userId] as const,
  authRecoveryCodes: (userId: string) => [...queryKeys.all, 'auth', 'recovery-codes', userId] as const,
  // auth.sessions is not in the supervisor's tableMap (the auth schema has no
  // realtime), so the Devices card invalidates this key after its own writes.
  authSessions: (userId: string) => [...queryKeys.all, 'auth', 'sessions', userId] as const,

  // ─── The founder's desk (admin_* RPCs; no table of theirs is in the
  // supervisor's tableMap, so the desk refetches after its own writes) ───
  adminIs: (userId: string) => [...queryKeys.all, 'admin', 'is', userId] as const,
  adminSettings: () => [...queryKeys.all, 'admin', 'settings'] as const,
  adminPulseAll: () => [...queryKeys.all, 'admin', 'pulse'] as const,
  adminPulse: (days: number) => [...queryKeys.adminPulseAll(), days] as const,
  adminUsers: () => [...queryKeys.all, 'admin', 'users'] as const,
  adminWaitlist: () => [...queryKeys.all, 'admin', 'waitlist'] as const,
  adminInvites: () => [...queryKeys.all, 'admin', 'invites'] as const,
  adminActions: () => [...queryKeys.all, 'admin', 'actions'] as const,
  /** The scheduled jobs and their last runs (admin_cron_health; the silence alarm, 2026-11-01). */
  adminCronHealth: () => [...queryKeys.all, 'admin', 'cron-health'] as const,
  adminReservedHandles: () => [...queryKeys.all, 'admin', 'reserved-handles'] as const,

  // ─── Collaborator roster (the bench: people + per-role rank) ───
  collaboratorRoster: (ownerId: string) => [...queryKeys.all, 'collaborator-roster', ownerId] as const,
  // Identity lookups (name + avatar) keyed by a stable, sorted id-set string.
  collaboratorIdentities: (idsKey: string) => [...queryKeys.all, 'collaborator-identities', idsKey] as const,

  // ─── Public / embed reads (unauthenticated) ───
  publicBusinessLookup: (handle: string) =>
    [...queryKeys.all, 'public', 'business', handle] as const,
  publicPackages: (userId: string, audienceSlug?: string) =>
    [...queryKeys.all, 'public', 'packages', userId, audienceSlug ?? 'all'] as const,
  // Server-published prices (public-price): region prices per package, the
  // priced extras shelf, menu "From" figures, discount hints. The browser's
  // ONLY price source on public surfaces since the 2026-08-13 repoint.
  publicServerPrices: (userId: string, audienceSlug?: string) =>
    [...queryKeys.all, 'public', 'server-prices', userId, audienceSlug ?? 'all'] as const,
  // The prefix of every creator's server prices: what a changed discount rule
  // invalidates (its row carries brand_id, not user_id) and what the discount
  // editor invalidates after a save, so the site and Preview Packages reprice
  // without a reload (Jota, 2026-10-21).
  publicServerPricesAll: () => [...queryKeys.all, 'public', 'server-prices'] as const,
  // Audience-derived public reads. All share the ['...','public','audience'] prefix so a
  // single audiences-table change invalidates every variant via partial-prefix matching.
  publicAudience: (userId: string, audienceSlug?: string) =>
    [...queryKeys.all, 'public', 'audience', 'one', userId, audienceSlug ?? 'all'] as const,
  brandAudiences: (brandId: string) =>
    [...queryKeys.all, 'public', 'audience', 'by-brand', brandId] as const,
  siteSections: (brandId: string) =>
    [...queryKeys.all, 'public', 'audience', 'sections', brandId] as const,
  publicPricingConfig: (userId: string, brandId?: string | null) =>
    // Brand-scoped since 2026-08-02 (booking rates are per brand). The extra
    // segment stays prefix-compatible with the supervisor's invalidation on
    // ['public','pricing-config',userId].
    [...queryKeys.all, 'public', 'pricing-config', userId, brandId ?? 'brand-default'] as const,
  // Tier ≤ 3 client-visible add-ons for one audience — the ONE optional-extras
  // source shared by the site section, the mega menu, and the booking wizard.
  publicOptionalExtras: (userId: string, audienceSlug?: string) =>
    [...queryKeys.all, 'public', 'optional-extras', userId, audienceSlug ?? 'all'] as const,
  // What the client's chosen extras cost on top of a package, priced by the
  // server. Keyed by the sorted extra ids so ticking a box is a new query and
  // unticking it returns a cached one.
  publicConfiguredPrice: (userId: string, packageId: string, audienceSlug: string, extraIds: string[]) =>
    [...queryKeys.all, 'public', 'configured-price', userId, packageId, audienceSlug,
      [...extraIds].sort().join(',')] as const,
  // Mega-menu projection inputs (useMenuModel). One catalog read per brand
  // owner (services split client-side), plus pages / journey stages / portfolio
  // counts per brand.
  publicMenuCatalog: (userId: string) =>
    [...queryKeys.all, 'public', 'menu-catalog', userId] as const,
  // The frozen page addresses of a creator's catalog (services.public_slug and its
  // history). Nested under the menu catalog key on purpose: the services
  // invalidator already reaches that prefix, so a changed address is live too.
  publicPageSlugs: (userId: string) =>
    [...queryKeys.publicMenuCatalog(userId), 'page-slugs'] as const,
  // Pages + their journey stages ride together (stages join via brand_pages_id).
  publicBrandPages: (brandId: string) =>
    [...queryKeys.all, 'public', 'brand-pages', brandId] as const,
  publicPortfolioLite: (brandId: string) =>
    [...queryKeys.all, 'public', 'portfolio', 'lite', brandId] as const,
  /** The person's posts of their content units (content_marks; THE_LIST #66 step 4). */
  contentMarks: (userId: string) => [...queryKeys.all, 'content', 'marks', userId] as const,
  /** The creator's saved shelf (saved_references; THE_LIST #62). */
  savedReferences: (userId: string) => [...queryKeys.all, 'content', 'saved', userId] as const,
  /** Which slot of the site each portfolio story was chosen for (portfolio_items.curation_slots). */
  portfolioCuration: (brandId: string) => [...queryKeys.all, 'portfolio', 'curation', brandId] as const,
  /** Booked work that could fill a slot: a brand's past, non-demo, confirmed bookings. */
  pastBookedWork: (brandId: string) => [...queryKeys.all, 'portfolio', 'past-work', brandId] as const,
  /** Bookings by the source tag they arrived with (bookings.booking_source; THE_LIST #65, #66). */
  bookingSources: (userId: string) => [...queryKeys.all, 'content', 'booking-sources', userId] as const,
  // The brand owner's portrait, shown wherever the site introduces the person
  // (brand home founder block, the derived About page). One key, one read.
  publicFounderAvatar: (ownerUserId: string) =>
    [...queryKeys.all, 'public', 'founder-avatar', ownerUserId] as const,
  // The creator's legal name and jurisdiction, through the public settings RPC:
  // the generated site's footer names who the business is.
  publicLegalIdentity: (userId: string) =>
    [...queryKeys.all, 'public', 'legal-identity', userId] as const,
  // Content translations for a brand, per active language. One fetch per public
  // page; folded into a flat bag and merged onto base rows at the hook boundary.
  publicTranslations: (userId: string, lang: string) =>
    [...queryKeys.all, 'public', 'translations', userId, lang] as const,
  // Authenticated translation-studio reads (owner editing their own translations,
  // per target language). Carries source_hash for staleness; optimistic writes.
  ownerTranslations: (userId: string, lang: string) =>
    [...queryKeys.all, 'owner-translations', userId, lang] as const,

  // ─── The colleague rate card (2026-11-02): the creator's numbers, the requests,
  //     and a visitor's read of one card through get_public_peer_rate_card. ───
  peerRateCard: (userId: string) => [...queryKeys.all, 'peer', 'rate-card', userId] as const,
  peerGigs: (userId: string) => [...queryKeys.all, 'peer', 'gigs', userId] as const,
  peerDatesBusy: (userId: string, dates: readonly string[]) =>
    [...queryKeys.all, 'peer', 'dates-busy', userId, [...dates].sort().join(',')] as const,
  publicPeerRateCard: (handle: string) => [...queryKeys.all, 'public', 'peer-rate-card', handle] as const,
  /** A colleague's page asking which of its dates are free (peer_dates_free). */
  publicPeerDatesFree: (handle: string, dates: readonly string[]) =>
    [...queryKeys.all, 'public', 'peer-dates-free', handle, [...dates].sort().join(',')] as const,

  // ─── Services / catalog ───
  services: (userId: string) => [...queryKeys.all, 'services', userId] as const,

  // ─── Pricing config ───
  pricingConfig: (userId: string) => [...queryKeys.all, 'pricing-config', userId] as const,

  // ─── The creator's Stripe account, as Contruly holds it (2026-10-01). The
  //     state lives on business_configuration.stripe_account_state, written by
  //     the edge only; the payouts table is one row per Stripe payout. Both are
  //     live through the supervisor, so the Payments card changes the moment
  //     Stripe tells us something, not the next time the creator looks. ───
  connectAccountState: (userId: string) => [...queryKeys.all, 'connect-account-state', userId] as const,
  // The on-demand re-read through check-connect-status; its result is written
  // into connectAccountState, never rendered from this key.
  connectAccountRefresh: (userId: string) => [...queryKeys.all, 'connect-account-refresh', userId] as const,
  payouts: (userId: string) => [...queryKeys.all, 'payouts', userId] as const,
  // Milestones clients owe in the next two weeks (payment_schedules, not in the
  // realtime publication: refetched on focus). Overview's Money on its way.
  moneyDue: (userId: string) => [...queryKeys.all, 'money-due', userId] as const,
  // business_events: things that happened to the creator's business, in the
  // creator's words (first producer: Stripe account state and payouts).
  businessEvents: (userId: string) => [...queryKeys.all, 'business-events', userId] as const,
  // The plan's milestones for one booking (payment_schedules): what is paid, what
  // is due, and what the couple marked as sent (step B, 2026-10-02).
  bookingMilestones: (bookingId: string) => [...queryKeys.all, 'booking-milestones', bookingId] as const,
  // The whole business_configuration row, as Operations and Profile's seller
  // card read it. Was a private ['business-config'] key nothing refreshed: with
  // a 5-minute staleTime, Operations could save five-minute-old tax rates over
  // a country change made in Profile (2026-10-03).
  businessConfiguration: (userId: string) => [...queryKeys.all, 'business-configuration', userId] as const,
  // The creator's own IBAN for bank transfers (business_configuration.transfer_*).
  transferDetails: (userId: string) => [...queryKeys.all, 'transfer-details', userId] as const,

  // ─── Financial foundation (goals: capacity + salary target). Shared cache so
  //     the capacity sliders and the break-even card read one live source. ───
  financialGoals: (userId: string) => [...queryKeys.all, 'financial-goals', userId] as const,

  // ─── History before Contruly (the import): jobs and the payments read for them ───
  historyJobs: (userId: string) => [...queryKeys.all, 'history-jobs', userId] as const,
  historyPayments: (userId: string) => [...queryKeys.all, 'history-payments', userId] as const,
  historyPriceLists: (userId: string) => [...queryKeys.all, 'history-price-lists', userId] as const,
  // The client cards read more columns than Insights, so they keep their own
  // entries under the same prefixes: invalidating historyJobs(userId) or
  // historyPayments(userId) after an import refreshes both pages.
  clientCardJobs: (userId: string) => [...queryKeys.historyJobs(userId), 'card'] as const,
  clientCardPayments: (userId: string) => [...queryKeys.historyPayments(userId), 'card'] as const,
  clientCardBookings: (userId: string) => [...queryKeys.all, 'bookings', 'client-cards', userId] as const,
  // Itemized expenses — the other half of the pricing foundation. Same
  // shared-cache contract as financialGoals; the realtime supervisor
  // invalidates it so no open tab can price from stale inputs.
  financialExpenses: (userId: string) => [...queryKeys.all, 'financial-expenses', userId] as const,
  // Sold capacity days (count of booked event dates in a year) — feeds the
  // unused-capacity report (Kaplan: idle capacity is REPORTED, never priced).
  soldDays: (userId: string, year: number) => [...queryKeys.all, 'sold-days', userId, year] as const,

  // ─── Google Calendar connection (settings) ───
  googleCalendar: (userId: string) => [...queryKeys.all, 'google-calendar', userId] as const,

  // ─── Portal service standards (client-facing contact, channels, FAQs) ───
  serviceStandards: (userId: string) => [...queryKeys.all, 'service-standards', userId] as const,

  // ─── Service assignments (operator dashboard) ───
  assignments: (brandId: string) => [...queryKeys.all, 'assignments', brandId] as const,
  assignmentsForBooking: (bookingId: string) =>
    [...queryKeys.all, 'assignments', 'booking', bookingId] as const,
  // ─── Crew asks (the availability cascade trail, keyed by the creator) ───
  crewAsks: (ownerId: string) => [...queryKeys.all, 'crew-asks', ownerId] as const,

  // ─── The creator's Bookings list. `bookings` is not in the realtime
  //     publication, so the page writes its own changes (Discard) into this
  //     cache; it was a private ['bookings-admin'] key until 2026-10-09. ───
  bookingsList: (userId: string) => [...queryKeys.all, 'bookings', 'list', userId] as const,

  // ─── Payments (A1 money-split) ───
  bookingPaymentSummary: (bookingId: string) =>
    [...queryKeys.all, 'payments', 'summary', bookingId] as const,

  // ─── Client obligations ("what we need from you") ───
  obligations: (bookingId: string) => [...queryKeys.all, 'obligations', 'booking', bookingId] as const,
  obligationsForUser: (userId: string) => [...queryKeys.all, 'obligations', 'user', userId] as const,

  // ─── Client Experience studio (journey reads; derived, never authored) ───
  journeyBookings: (userId: string) => [...queryKeys.all, 'journey', 'bookings', userId] as const,
  journey: (bookingId: string) => [...queryKeys.all, 'journey', 'booking', bookingId] as const,
  voiceTemplates: (brandId: string) => [...queryKeys.all, 'journey', 'voice', brandId] as const,
  journeyQueue: (userId: string) => [...queryKeys.all, 'journey', 'queue', userId] as const,
  journeyRules: (userId: string) => [...queryKeys.all, 'journey', 'rules', userId] as const,
  brandTheme: (brandId: string) => [...queryKeys.all, 'journey', 'theme', brandId] as const,

  // ─── Deliverable feedback (Phase 3 approval / revisions) ───
  deliverableFeedback: (bookingId: string) =>
    [...queryKeys.all, 'deliverable-feedback', 'booking', bookingId] as const,
  deliverableFeedbackForUser: (userId: string) =>
    [...queryKeys.all, 'deliverable-feedback', 'user', userId] as const,
  // Portal-side feedback + acceptance + revision terms (via portal-feedback edge
  // fn). Distinct from the creator key above — different shape, manual refetch
  // (portal visitors aren't supervisor-subscribed to deliverable_feedback).
  portalFeedback: (bookingId: string) =>
    [...queryKeys.all, 'portal', 'feedback', bookingId] as const,

  // ─── The owner's brand languages (translation studio) ───
  // Written by the studio's toggles; the v2_brands invalidator refetches it.
  brandLanguages: (ownerUserId: string) => [...queryKeys.all, 'brand-languages', ownerUserId] as const,

  // ─── Deliverables (the films, galleries, albums a booking owes) ───
  // Per booking for the project manager, per creator for the load gauge; the
  // supervisor invalidates both from one `deliverables` row (tableMap).
  deliverables: (bookingId: string) => [...queryKeys.all, 'deliverables', 'booking', bookingId] as const,
  deliverablesForUser: (userId: string) => [...queryKeys.all, 'deliverables', 'user', userId] as const,

  // ─── Active policies a client must accept (wizard consent step) ───
  // One key for the list the checkboxes render AND the list the step gate
  // counts; keyed by the creative, read by both through useActivePolicies.
  activePolicies: (creativeId: string) => [...queryKeys.all, 'policies', 'active', creativeId] as const,
} as const;
